import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';

import { useTriggerAuditMutation, type AuditTriggerResult } from '../../api/audit-config-queries';
import { useErrorSnackbar } from '../ErrorSnackbar';
import WriteGuard from '../WriteGuard';
import { useWritePermission } from '../../api/permission-queries';
import { describeApiError } from '../../api/apiClient';

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}

export default function RunAuditSection() {
  const theme = useTheme();
  const { showError } = useErrorSnackbar();
  const triggerMutation = useTriggerAuditMutation();
  const permission = useWritePermission();
  const [lastResult, setLastResult] = useState<AuditTriggerResult | null>(null);

  async function handleRunAudit() {
    setLastResult(null);
    try {
      const result = await triggerMutation.mutateAsync();
      setLastResult(result);
    } catch (error: unknown) {
      showError(`Audit run failed — ${describeApiError(error)}`);
    }
  }

  const failedPlatforms = lastResult?.platforms.filter((p) => !p.success) ?? [];

  return (
    <Box>
      {/* Trigger Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="h6" sx={{ fontSize: '0.95rem', mb: 1 }}>
            Run Audit
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, fontSize: '0.8rem' }}>
            Manually trigger a full audit across all platforms. This runs the same pipeline as the
            scheduled audit and persists results to Table Storage. Typically completes in 30–90
            seconds.
          </Typography>

          <WriteGuard reason={permission.reason}>
            <Button
              variant="contained"
              onClick={handleRunAudit}
              disabled={triggerMutation.isPending || !permission.canWrite}
              startIcon={
                triggerMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {triggerMutation.isPending ? 'Running Audit...' : 'Run Audit Now'}
            </Button>
          </WriteGuard>
        </CardContent>
      </Card>

      {/* Result Card */}
      {lastResult && (
        <Card>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '0.95rem' }}>
                Audit Result
              </Typography>
              <Chip
                label="Complete"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.main,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 2,
                mb: 2,
              }}
            >
              <StatBox label="Devices" value={lastResult.summary.totalDevices} />
              <StatBox label="Clients" value={lastResult.summary.clientCount} />
              <StatBox label="Coverage" value={`${lastResult.summary.coveragePercentage}%`} />
              <StatBox label="Critical" value={lastResult.summary.criticalCount} />
              <StatBox label="Duration" value={formatDuration(lastResult.durationMs)} />
            </Box>

            {/* Platform breakdown */}
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.8rem',
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Platforms:{' '}
              {lastResult.platforms
                .map(
                  (p) =>
                    `${p.platform} (${p.success ? `${p.deviceCount} devices` : `failed: ${p.error}`})`
                )
                .join(', ')}
            </Typography>

            {failedPlatforms.length > 0 && (
              <Typography variant="body2" sx={{ color: 'warning.main', fontSize: '0.8rem', mt: 1 }}>
                {failedPlatforms.length} platform(s) failed — partial results persisted
              </Typography>
            )}

            {lastResult.summary.integrity && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    color:
                      lastResult.summary.integrity.issueInvariantViolations > 0
                        ? 'error.main'
                        : 'success.main',
                  }}
                >
                  Issue check:{' '}
                  {lastResult.summary.integrity.issueInvariantViolations === 0
                    ? 'every issue matches its device record'
                    : `${lastResult.summary.integrity.issueInvariantViolations} issue(s) contradict their device record`}
                </Typography>
                {lastResult.summary.integrity.staleIssueRowsDeleted > 0 && (
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                    Removed {lastResult.summary.integrity.staleIssueRowsDeleted} stale issue row(s)
                    left by earlier runs:{' '}
                    {Object.entries(lastResult.summary.integrity.staleIssueRowsByClient)
                      .map(
                        ([client, byType]) =>
                          `${client.replace(/^client-/, '').toUpperCase()} (${Object.entries(byType)
                            .map(([type, count]) => `${type} ×${count}`)
                            .join(', ')})`
                      )
                      .join('; ')}
                  </Typography>
                )}
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                mt: 1.5,
                fontFamily: 'var(--font-mono)',
              }}
            >
              Run ID: {lastResult.auditRunId}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{value}</Typography>
    </Box>
  );
}
